/* @flow */
/**
 * Redux Toolkit slice and async actions for PPM report searches, saved searches, filters, export,
 * and pagination.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  toggleSpinner, updateDialogStates, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import { initialPPMReportFieldsState, ppmReportFieldList } from '../../constants/staticData';
import {
  checkForColumnData,
  getFilterValuesFromResponse, mapInputFieldsToRequest, mapRequestToInputFields,
  mapFiltersToRequest,
  sortedFilterData,
  setFieldItemVas,
  setFilterAsiaSize,
  setValidFields
} from '../../helpers/dataHelpers';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import appConfig from '../../appConfig';
import PPMSearchDataMock from '../mock/PPMSearchDataMock';
import serviceAPI from '../../helpers/serviceAPI';
import { showForBIS, showForGoodSupplier } from '../../helpers/envServer';
import ppmReportsHeader from '../../constants/ppmReportsHeader';
import ppmReportFieldMapping from '../../constants/fieldMappings/ppmReportFieldMapping';
import ppmReportFieldProperties from '../../constants/fieldProperties/ppmReportFieldProperties';
import {
  getFieldsList, getFiltersInResponseRequestBody, getRequestedFilterValues,
  getSelectedFilterValues, getSelectedFilterValuesFromOpMetrics,
  getFiltersForRequest, getModifiedSelectedFilters, getSelectedFilterKeys,
  asyncCallToNewFiltersCallback
} from './ppmHelper';
import { settingsActions } from '../profileSettings/settingSlice';
import {
  removeFieldAsiaSize, removeFieldShipToAddress, setFieldAsiaSize, setFieldShipToAddress
} from '../poSearch/poSearchHelper';

export const name = 'ppmReport';

const excludedPPMReportExportFields = new Set(appConfig.excludedPPMReportFieldsInExportedFile);
const includeFieldsPPMSet = new Set(appConfig.includeFieldsPPM);

/**
 * Generates a short id used to force dependent PPM report UI refreshes.
 */
const getRandomSearchId = () => cryptoRand().toString().slice(2, 10);

/**
 * Determines whether jump-to-page pagination needs additional next or previous API prefetches.
 */
function getJumpToPageApiCallFlags(page: number, rowPerPage: number) {
  // Prefetch boundaries depend on page groups represented by each row-count option.
  const numberString = page?.toString();

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString.endsWith('9');
    checkApiCallPrepend = numberString.endsWith('0');
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return { checkApiCallApend, checkApiCallPrepend };
}

/**
 * Maps saved filter values to matching values available in the latest filter response.
 */
function mapSavedSearchFilterValues(
  filterValues: Array<Object>,
  availableFilters: Array<Object> = []
) {
  return filterValues
    .map((filterValue) => availableFilters.find(
      (availableFilter) => availableFilter.includes(filterValue)
    ))
    .filter(Boolean);
}

/**
 * Builds selected filter state from saved search filters and returned dropdown values.
 */
function buildSelectedFiltersFromSavedSearch(
  filters: Array<Object>,
  filterValuesFromResponse: Object
) {
  const selectedFilters = {};
  filters.forEach((filter) => {
    selectedFilters[filter.fieldName] = mapSavedSearchFilterValues(
      filter.fieldValue,
      filterValuesFromResponse[filter.fieldName]
    );
  });
  return selectedFilters;
}

function createInitialState() {
  return {
    isSavedSearchModified: false,
    ppmReportRequestBody: null,
    ppmReportInputFieldData: { ...initialPPMReportFieldsState },
    ppmReportSelectedFields: [],
    ppmReportBookmarkName: '',
    ppmReportBookmarkId: '',
    requestError: false,

    ppmReportResultData: {},
    ppmReportSearchId: '',
    ppmReportTotalCount: 0,
    ppmReportNextResults: {},
    ppmReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    ppmReportSearchPanelId: '',

    ppmReportFilterId: 'none',
    filterDropdownValues: {},
    selectedFilters: {
      [ppmReportFieldMapping.PPM_REPORT_ITEM_STATUS]: null,
      [ppmReportFieldMapping.PPM_REPORT_PLANNING_PRIORITY_NUMBER]: null,
      [ppmReportFieldMapping.PPM_REPORT_TRANSPORT_MODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_PLANT_CODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_CUSTOMER_SHIP_TO]: null,
      [ppmReportFieldMapping.PPM_REPORT_LAUNCH]: null,
      [ppmReportFieldMapping.PPM_REPORT_SUB_CATEGORY_CODE]: null,
      ...(showForBIS && {
        [ppmReportFieldMapping.PPM_REPORT_CONSUMER_CONSTRUCT_DIMENSION_NAME]: null
      }),
      [ppmReportFieldMapping.PPM_REPORT_SIZE_MISMATCH_ID]: null,
      [ppmReportFieldMapping.PPM_REPORT_DESTINATION_COUNTRY_CODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_PO_REJECTION_CODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_OVERDUE_INDICATOR]: null,
      [ppmReportFieldMapping.PPM_REPORT_TITLE_TRANSFER_MODEL_CODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_FOB_MISMATCH_INDICATOR]: null,
      [ppmReportFieldMapping.PPM_REPORT_USECASE_CODE]: null,
      [ppmReportFieldMapping.PPM_REPORT_ASIAN_CC]: null,
      ...(showForBIS && {
        [ppmReportFieldMapping.PPM_REPORT_BIS_CLASSIFICATION]: null
      }),
      ...(showForGoodSupplier && {
        [ppmReportFieldMapping.PPM_REPORT_GOODS_SUPPLIER_CODE]: null
      })
    },
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false,
    isPPMReportFilterBarOpen: false
  };
}

export const initialState = createInitialState();

/**
 * Reducer that updates report filters request in PPM report state from the action payload.
 */
function updateReportFiltersRequest(state: Object, action: Object) {
  const updatedBody = { ...state.ppmReportRequestBody, filter: action.payload };
  return {
    ...state,
    ppmReportRequestBody: updatedBody,
    isSavedSearchModified: Boolean(state.ppmReportBookmarkName)
  };
}

/**
 * Reducer that updates PPM page in PPM report state from the action payload.
 */
function updatePPMPage(state: Object, action: Object) {
  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that updates PPM rows per page in PPM report state from the action payload.
 */
function updatePPMRowsPerPage(state: Object, action: Object) {
  return {
    ...state,
    rowsPerPage: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that updates PPM report request body in PPM report state from the action payload.
 */
function updatePPMReportRequestBody(state: Object, action: Object) {
  // Compare serialized bodies to detect edits to an active saved search.
  const requestExist = JSON.stringify(state.ppmReportRequestBody);
  return {
    ...state,
    ppmReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.ppmReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}

/**
 * Reducer that updates is saved search modified in PPM report state from the action payload.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Reducer that updates PPM report request body on same bookmark in PPM report state from the action
 * payload.
 */
function updatePPMReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  return {
    ...state,
    ppmReportRequestBody: action.payload
  };
}

/**
 * Reducer that clears filters in PPM report state.
 */
function clearFilters(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    selectedFilters[filter] = null;
  });
  return {
    ...state,
    filterDropdownValues: {},
    selectedFilters
  };
}

/**
 * Reducer that resets PPM report results in PPM report state.
 */
function resetPPMReportResults(state: Object) {
  return {
    ...state,
    ppmReportResultData: {},
    ppmReportSearchId: '',
    ppmReportTotalCount: 0,
    ppmReportNextResults: {},
    ppmReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    ppmReportSearchPanelId: ''
  };
}

/**
 * Reducer that resets PPM report request in PPM report state.
 */
function resetPPMReportRequest(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    ppmReportRequestBody: null,
    ppmReportInputFieldData: { ...initialPPMReportFieldsState },
    ppmReportSelectedFields: [],
    ppmReportBookmarkName: '',
    ppmReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Reducer that resets PPM report request for clear search in PPM report state.
 */
function resetPPMReportRequestForClearSearch(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    ppmReportRequestBody: null,
    ppmReportInputFieldData: { ...initialPPMReportFieldsState },
    ppmReportBookmarkName: '',
    ppmReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Reducer that updates PPM report offset in request in PPM report state from the action payload.
 */
function updatePPMReportOffsetInRequest(state: Object, action: Object) {
  const updatedBody = { ...state.ppmReportRequestBody, offset: action.payload };
  return { ...state, ppmReportRequestBody: updatedBody };
}

/**
 * Reducer that updates PPM report fields in PPM report state from the action payload.
 */
function updatePPMReportFields(state: Object, action: Object) {
  return {
    ...state,
    ppmReportSelectedFields: action.payload
  };
}

/**
 * Reducer that updates PPM report input values in PPM report state from the action payload.
 */
function updatePPMReportInputValues(state: Object, action: Object) {
  return {
    ...state,
    ppmReportInputFieldData: action.payload
  };
}

/**
 * Reducer that updates collab bookmark id in PPM report state from the action payload.
 */
function updateCollabBookmarkId(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    ppmReportBookmarkId: action.payload
  };
}

/**
 * Reducer that updates collab bookmark name in PPM report state from the action payload.
 */
function updateCollabBookmarkName(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    ppmReportBookmarkName: action.payload
  };
}

/**
 * Reducer that updates report filter drop down values in PPM report state from the action payload.
 */
function updateReportFilterDropDownValues(state: Object, action: Object) {
  if (Object.keys(action.payload ?? {}).length > 0) {
    return {
      ...state,
      filterDropdownValues: sortedFilterData(action.payload)
    };
  }
  return { ...state };
}

/**
 * Reducer that updates apply bulk selected filters update in PPM report state.
 */
function applyBulkSelectedFiltersUpdate(state: Object, action: Object) {
  if (Array.isArray(action.payload)) {
    // Array payloads represent enabled filter keys, not selected option values.
    const selectedFilterSet = new Set(action.payload);
    const modifiedSelectedFilters = { ...state.selectedFilters };
    Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
      if (selectedFilterSet.has(filter)) {
        if (!modifiedSelectedFilters[filter]) modifiedSelectedFilters[filter] = [];
      } else {
        modifiedSelectedFilters[filter] = null;
      }
    });
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  if (typeof action.payload === 'object' && action.payload.selectedFilters
    && Object.keys(action.payload.selectedFilters).length > 0) {
    const modifiedSelectedFilters = {
      ...state.selectedFilters,
      ...action.payload.selectedFilters
    };
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  return { ...state, selectedFilters: initialState.selectedFilters };
}

/**
 * Reducer that applies bulk update selected PPM filters changes in PPM report state.
 */
function bulkUpdateSelectedPPMFilters(state: Object, action: Object) {
  return applyBulkSelectedFiltersUpdate(state, action);
}

/**
 * Reducer that updates PPM report results in PPM report state from the action payload.
 */
function updatePPMReportResults(state: Object, action: Object) {
  return {
    ...state,
    ppmReportResultData: action.payload,
    ppmReportSearchId: getRandomSearchId(),
    ppmReportTotalCount: action.payload.pages.totalResources,
    selectedRecordsCount: 0,
    ppmReportNextResults: null,
    ppmReportPrevResults: null
  };
}

/**
 * Reducer that updates PPM report search id in PPM report state from the action payload.
 */
function updatePPMReportSearchId(state: Object) {
  return { ...state, ppmReportSearchId: getRandomSearchId() };
}

/**
 * Reducer that resets PPM report search panel id in PPM report state.
 */
function resetPPMReportSearchPanelId(state: Object) {
  return { ...state, ppmReportSearchPanelId: getRandomSearchId() };
}

/**
 * Reducer that resets PPM report input data in PPM report state.
 */
function resetPPMReportInputData(state: Object) {
  return {
    ...state,
    ppmReportInputFieldData: { ...initialPPMReportFieldsState }
  };
}

/**
 * Reducer that resets PPM report selected filters to initial state in PPM report state.
 */
function resetPPMReportSelectedFiltersToInitialState(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    if (state.selectedFilters[filter]) {
      selectedFilters[filter] = [];
    } else {
      selectedFilters[filter] = null;
    }
  });
  return {
    ...state,
    selectedFilters
  };
}

/**
 * Reducer that updates PPM report next result in PPM report state from the action payload.
 */
function updatePPMReportNextResult(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    // Keep the fetched page cached until the UI appends it.
    dataNext: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportNextResults: payload.dataNext,
    ppmReportTotalPages: payload.data.pages.totalPages,
    ppmReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that updates PPM report prev result in PPM report state from the action payload.
 */
function updatePPMReportPrevResult(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportPrevResults: payload.dataPrev,
    ppmReportTotalPages: payload.data.pages.totalPages,
    ppmReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Reducer that clears saved PPM report in PPM report state.
 */
function clearSavedPPMReport(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    ppmReportBookmarkId: '',
    ppmReportBookmarkName: ''
  };
}

/**
 * Reducer that updates PPM report offset in PPM report state.
 */
function ppmReportOffset(state: Object, action: Object) {
  return {
    ...state,
    ppmReportResultData: action.payload,
    ppmReportTotalCount: action.payload.pages.totalResources,
    selectedRecordsCount: 0,
    ppmReportNextResults: null,
    ppmReportPrevResults: null
  };
}

/**
 * Reducer that promotes the cached next PPM report page and stores the current page as previous
 * cache.
 */
function appendNextResultSet(state: Object) {
  const payload = {
    dataPrev: state.ppmReportResultData,
    data: state.ppmReportNextResults
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportPrevResults: payload.dataPrev,
    ppmReportNextResults: null
  };
}

/**
 * Reducer that promotes the cached next PPM report page and updates jump-to-page prefetch flags.
 */
function appendJumpToPageNextResultSet(state: Object, action: Object) {
  const updatedCurrentResultSet = state.ppmReportResultData;
  const updatedNextResultSet = state.ppmReportNextResults;
  // Jump-to-page appends may land on a boundary that requires another prefetch.
  const { checkApiCallApend, checkApiCallPrepend } = getJumpToPageApiCallFlags(
    state.page,
    action.payload
  );

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportPrevResults: payload.dataPrev,
    ppmReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that promotes the cached previous PPM report page and stores the current page as next
 * cache.
 */
function appendPrevResultSet(state: Object) {
  const payload = {
    data: state.ppmReportPrevResults,
    dataNext: state.ppmReportResultData
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportNextResults: payload.dataNext,
    ppmReportPrevResults: null
  };
}

/**
 * Reducer that promotes the cached previous PPM report page and updates jump-to-page prefetch
 * flags.
 */
function appendJumpToPagePrevResultSet(state: Object, action: Object) {
  const updatedPrevResultSet = state.ppmReportPrevResults;
  const updatedCurrentResultSet = state.ppmReportResultData;
  // Jump-to-page appends may land on a boundary that requires another prefetch.
  const { checkApiCallApend, checkApiCallPrepend } = getJumpToPageApiCallFlags(
    state.page,
    action.payload
  );

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    ppmReportResultData: payload.data,
    ppmReportNextResults: payload.dataNext,
    ppmReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that updates next result setto null in PPM report state from the action payload.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    ppmReportNextResults: null
  };
}

/**
 * Reducer that updates previous result setto null in PPM report state from the action payload.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    ppmReportPrevResults: null
  };
}

/**
 * Reducer that updates jump to page in PPM report state from the action payload.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}
/**
 * Reducer that updates trigger jump to page in PPM report state from the action payload.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that updates API call after append in PPM report state from the action payload.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that updates API call before prepend in PPM report state from the action payload.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Reducer that updates set PPM report filter id in PPM report state.
 */
function setPpmReportFilterId(state: Object) {
  return {
    ...state,
    ppmReportFilterId: getRandomSearchId()
  };
}

/**
 * Reducer that applies bulk update report filter values changes in PPM report state.
 */
function bulkUpdateReportFilterValues(state: Object, action: Object) {
  if (!action.payload.filter) return { ...state };
  const modifiedSelectedFilters = {
    ...state.selectedFilters,
    [action.payload.filter]: action.payload.values
  };
  return { ...state, selectedFilters: modifiedSelectedFilters };
}

/**
 * Reducer that applies bulk toggle filters changes in PPM report state.
 */
function bulkToggleFilters(state: Object, action: Object) {
  const modifiedSelectedFilters = { ...state.selectedFilters };
  Object.keys(action.payload).forEach((filter) => {
    if (action.payload[filter]) {
      if (!modifiedSelectedFilters[filter]) modifiedSelectedFilters[filter] = [];
    } else modifiedSelectedFilters[filter] = null;
  });
  return { ...state, selectedFilter: modifiedSelectedFilters };
}
/**
 * Reducer that updates filtersInResponse on the stored PPM report request body.
 */
function updateReportFiltersInResposne(state: Object, action: Object) {
  // UPDATE_PPM_REPORT_FILTERSINRESPONSE_IN_REQUEST
  const updatedBody = { ...state.ppmReportRequestBody, filtersInResponse: action.payload };
  return { ...state, ppmReportRequestBody: updatedBody };
}

/**
 * Reducer that applies bulk update selected filters changes in PPM report state.
 */
function bulkUpdateSelectedFilters(state: Object, action: Object) {
  return applyBulkSelectedFiltersUpdate(state, action);
}

/**
 * Reducer that clears cached PPM report filter dropdown values.
 */
function RESET_PPM_REPORT_FILTER_DROPDOWN_VAUES(state: Object) {
  return {
    ...state,
    filterDropdownValues: {}
  };
}

/**
 * Reducer that updates PPM report is filter bar open in PPM report state from the action payload.
 */
function updatePPMReportIsFilterBarOpen(state: Object, action: Object) {
  return {
    ...state,
    isPPMReportFilterBarOpen: action.payload
  };
}

/**
 * Collects slice reducers for createSlice registration.
 */
function createReducers() {
  return {
    updatePPMReportInputValues,
    updatePPMReportFields,
    updatePPMReportRequestBody,
    updateIsSavedSearchModified,
    updatePPMReportRequestBodyOnSameBookmark,
    updateCollabBookmarkId,
    updateCollabBookmarkName,
    resetPPMReportRequest,
    resetPPMReportInputData,
    clearSavedPPMReport,
    resetPPMReportRequestForClearSearch,
    updatePPMReportOffsetInRequest,
    updateReportFiltersRequest,

    updatePPMPage,
    updatePPMRowsPerPage,
    resetPPMReportResults,
    updatePPMReportResults,
    updatePPMReportSearchId,
    resetPPMReportSearchPanelId,
    updatePPMReportNextResult,
    updatePPMReportPrevResult,
    ppmReportOffset,
    appendNextResultSet,
    appendPrevResultSet,
    setPpmReportFilterId,
    updateReportFilterDropDownValues,
    bulkUpdateReportFilterValues,
    bulkToggleFilters,
    updateReportFiltersInResposne,
    bulkUpdateSelectedFilters,
    clearFilters,
    resetPPMReportSelectedFiltersToInitialState,
    RESET_PPM_REPORT_FILTER_DROPDOWN_VAUES,
    bulkUpdateSelectedPPMFilters,
    appendJumpToPageNextResultSet,
    appendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updateNextResultSettoNull,
    updatePreviousResultSettoNull,
    updatePPMReportIsFilterBarOpen
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

// ppmReportResults -- start
/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function callPPMReportSearchAPI() {
  return createAsyncThunk(
    `${name}/callPPMReportSearchAPI`,
    async ({
      requestBody,
      rowsPerPageOption,
      filterOptions,
      shouldRequestFilterValues,
      callback,
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      let payload = { ...requestBody };
      // Remove fields disabled by environment or feature flags before submitting.
      payload = setValidFields(ppmReportFieldProperties, payload, [], inactiveFeatureFields);
      payload = setFieldItemVas(payload);
      payload = setFieldAsiaSize(payload);
      payload = setFilterAsiaSize(payload);
      payload = setFieldShipToAddress(payload);
      const requestData = {
        ...payload,
        fields: [
          ...payload.fields,
          ...appConfig.includeFieldsPPM.filter(
            (item) => !includeFieldsPPMSet.has(item)
          )
        ]
      };
      if (serviceAPI('ppmReportSearch').mock) {
        dispatch(actions.updatePPMReportRequestBody(payload));
        dispatch(toggleSpinner(false));
        dispatch(actions.updatePPMRowsPerPage(rowsPerPageOption));
        dispatch(actions.updatePPMReportResults(PPMSearchDataMock.data));
      } else {
        request({
          api: 'ppmReportSearch',
          method: 'post',
          data: requestData,
          cancellable: true
        }, dispatch, getState).then((response: Object) => {
          payload = removeFieldAsiaSize(payload);
          payload = removeFieldShipToAddress(payload);
          dispatch(toggleSpinner(false));
          dispatch(actions.updatePPMReportRequestBody(payload));
          callback(response.data);
          dispatch(actions.updatePPMRowsPerPage(rowsPerPageOption));
          if (shouldRequestFilterValues) {
            dispatch(actions.updateReportFilterDropDownValues(
              getFilterValuesFromResponse(response.data.filters || [])
            ));
          }
          if (shouldRequestFilterValues && filterOptions?.length > 0) {
            dispatch(actions.bulkUpdateSelectedPPMFilters(filterOptions));
          }
          dispatch(actions.resetPPMReportSearchPanelId());
          dispatch(actions.updatePPMReportResults(
            getflattenResponse(response.data)
          ));
        }).catch((error: Object) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
      }
    }
  );
}

/**
 * Creates a thunk that builds PPM report search criteria and dispatches the search API thunk.
 */
function fetchPOReports() {
  return createAsyncThunk(
    `${name}/fetchPOReports`,
    async ({
      data, callback, modifiedrequestBody = {},
      shouldRequestFilterValues = true, ppmCallPPMReportSearchAPI,
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      const state = getState();
      const secondaryFields = [];
      getFieldsList(secondaryFields);
      let requestData = {};
      const {
        selectedColumnOrderOptions,
        selectedFilterOptions: filterOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};

      let columnOrderOptions = [...selectedColumnOrderOptions];

      if (checkForColumnData(state, true)) {
        ppmReportsHeader.forEach((headRow: Object) => {
          if (headRow.includeInRequest) {
            columnOrderOptions = [...columnOrderOptions, headRow.primary];
          }
        });
      }

      const fieldsForRequest = checkForColumnData(state, true)
        ? columnOrderOptions
        : secondaryFields;

      if (data) {
        requestData = mapInputFieldsToRequest(data, 'ppmReport');
        // Exclude deleted PPM records from normal report searches.
        requestData.push({
          fieldName: ppmReportFieldMapping.PPM_REPORT_DELETION_INDICATOR,
          function: 'NOT IS_DEFINED'
        });
        const appliedSearchField = new Set(requestData.map((field) => field.fieldName));
        dispatch(actions.updatePPMReportInputValues(data));
        dispatch(actions.updatePPMReportFields(ppmReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedSearchField.has(field))));
      } else {
        requestData = state.ppmReport.ppmReportRequestBody.search;
      }

      const filterRequest = Object.keys(modifiedrequestBody).length
        ? state.ppmReport.ppmReportRequestBody.filter
        : [];
      const requestBody = (state.ppmReport.ppmReportRequestBody) ? {
        ...state.ppmReport.ppmReportRequestBody,
        search: requestData,
        filter: filterRequest,
        offset: '0',
        ...modifiedrequestBody
      } : {
        fields: fieldsForRequest,
        search: requestData,
        filter: filterRequest,
        count: appConfig.SearchThresholdLimit,
        offset: '0',
        ...modifiedrequestBody
      };
      if (!filterOptions?.length) {
        if (shouldRequestFilterValues) {
          getRequestedFilterValues(requestBody, state);
        }
      } else {
        getSelectedFilterValues(requestBody, state);
      }

      dispatch(ppmCallPPMReportSearchAPI({
        requestBody,
        rowsPerPageOption,
        filterOptions,
        shouldRequestFilterValues,
        callback,
        inactiveFeatureFields
      }));
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportExportLargeFile API for the PPM report workflow and
 * updates Redux state.
 */
function exportPOReport() {
  return createAsyncThunk(
    `${name}/exportPOReport`,
    async ({
      callback,
      filetype,
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      const state = getState();
      const searchRequestBody = {
        ...state.ppmReport.ppmReportRequestBody
      };
      let requestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: [...(searchRequestBody.fields) || []].filter((field: string) => (
          !excludedPPMReportExportFields.has(field)
        )),
        filter: [...(searchRequestBody.filter) || []],
        searchType: searchRequestBody.searchType,
        fileFormat: filetype
      };
      const filteredFieldsData = [...requestBody.fields].filter(
        (item) => !item.toLowerCase().includes('ratetypename')
      );
      requestBody = { ...requestBody, fields: filteredFieldsData };
      requestBody = setValidFields(
        ppmReportFieldProperties,
        requestBody,
        [],
        inactiveFeatureFields
      );
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldShipToAddress(requestBody);
      requestBody = setFieldItemVas(requestBody);

      request({
        api: 'ppmReportExportLargeFile',
        method: 'post',
        data: requestBody
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        callback(response);
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
        callback(null, error);
      });
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function callPPMReportSavedSearchAPI() {
  return createAsyncThunk(
    `${name}/callPPMReportSavedSearchAPI`,
    async ({
      savedSearchRequestBody,
      requestBody,
      ppmReportRequestData,
      rowsPerPageOption,
      filterOptions,
      callback,
      inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      const filteredFieldsData = [...savedSearchRequestBody.fields].filter(
        (item) => !item.toLowerCase().includes('ratetypename')
      );
      let payload = { ...savedSearchRequestBody, fields: filteredFieldsData };
      // Remove fields disabled by environment or feature flags before submitting.
      payload = setValidFields(ppmReportFieldProperties, payload, [], inactiveFeatureFields);
      payload = setFieldItemVas(payload);
      payload = setFieldAsiaSize(payload);
      payload = setFilterAsiaSize(payload);
      payload = setFieldShipToAddress(payload);
      const requestData = {
        ...payload,
        fields: [
          ...payload.fields,
          ...appConfig.includeFieldsPPM.filter(
            (item) => !includeFieldsPPMSet.has(item)
          )
        ]
      };
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestData,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        const state = getState();
        const fields = mapRequestToInputFields([...ppmReportRequestData.searchCriteria.search]);
        const ppmReportSearchFields = new Set(
          ppmReportRequestData.searchCriteria.search.map((field: Object) => field.fieldName)
        );
        dispatch(actions.updatePPMReportInputValues({
          ...fields
        }));
        let updatedRequestBody = setValidFields(ppmReportFieldProperties, requestBody);
        updatedRequestBody = setFieldItemVas(updatedRequestBody);
        updatedRequestBody = removeFieldAsiaSize(updatedRequestBody);
        updatedRequestBody = removeFieldShipToAddress(updatedRequestBody);
        dispatch(actions.updatePPMReportRequestBody(updatedRequestBody));
        dispatch(actions.updatePPMReportFields(ppmReportFieldList
          .map((field: Object) => field.key)
          .filter((field: Object) => ppmReportSearchFields.has(field))));
        dispatch(actions.updatePPMReportResults(
          getflattenResponse(response.data)
        ));
        dispatch(actions.updatePPMReportSearchId());
        dispatch(actions.updatePPMPage(0));
        dispatch(actions.updatePPMRowsPerPage(rowsPerPageOption));
        dispatch(actions.updateCollabBookmarkId(ppmReportRequestData.id));
        dispatch(actions.updateCollabBookmarkName(ppmReportRequestData.name));
        dispatch(toggleSpinner(false));
        const filterValuesFromResponse = getFilterValuesFromResponse(response.data.filters || []);
        dispatch(actions.updateReportFilterDropDownValues({
          ...state.ppmReport.filterDropdownValues,
          ...filterValuesFromResponse
        }));
        const filters = ppmReportRequestData?.searchCriteria?.filter;
        const selectedFilters = filters?.length > 0
          ? buildSelectedFiltersFromSavedSearch(filters, filterValuesFromResponse)
          : {};
        dispatch(actions.bulkUpdateSelectedPPMFilters({
          selectedFilters
        }));

        if (filterOptions.length > 0) {
          dispatch(actions.bulkUpdateSelectedPPMFilters(filterOptions));
        }
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Creates a thunk that prepares saved PPM report criteria and dispatches the saved-search API
 * thunk.
 */
function fetchSavedPPMReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedPPMReportResults`,
    async (
      { ppmReportRequestData, callback, ppmcallPPMReportSavedSearchAPI },
      { getState, dispatch }
    ) => {
      const requestBody = { ...ppmReportRequestData.searchCriteria, offset: '0' };
      if (Array.isArray(requestBody.search)
        && !requestBody.search.find(
          (criteria) => criteria.fieldName === ppmReportFieldMapping.PPM_REPORT_DELETION_INDICATOR
        )
      ) {
        requestBody.search.push({
          fieldName: ppmReportFieldMapping.PPM_REPORT_DELETION_INDICATOR,
          function: 'NOT IS_DEFINED'
        });
      }

      const state = getState();
      const {
        selectedFilterOptions: filterOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));

      // Merge saved-search filters with the user's default filter selections.
      const filterValuesOptByUser = getSelectedFilterValuesFromOpMetrics(filterOptions);
      const filtersInResponseData = getFiltersInResponseRequestBody(
        ppmReportRequestData.searchCriteria.filter
      );
      // to include user default filters only for LISR
      if (filterOptions?.length > 0) {
        filtersInResponseData.push(...filterValuesOptByUser);
      }

      const filteredData = [...new Set(filtersInResponseData.map(({ primary }) => primary)
        .filter(Boolean))]
        .map((primary) => filtersInResponseData.find(({ primary: p }) => p === primary));

      const savedSearchRequestBody = {
        savedSearchID: ppmReportRequestData.id,
        ...requestBody,
        filtersInResponse: filteredData
      };

      dispatch(ppmcallPPMReportSavedSearchAPI({
        savedSearchRequestBody,
        requestBody,
        ppmReportRequestData,
        rowsPerPageOption,
        filterOptions,
        state,
        callback
      }));
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function fetchPpmReportsOffsetApi() {
  return createAsyncThunk(
    `${name}/fetchPpmReportsOffsetApi`,
    async ({ updatedRequest }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: updatedRequest
      }, dispatch, getState).then((response: Object) => {
        dispatch(actions.ppmReportOffset(getflattenResponse(response.data)));
        dispatch(toggleSpinner(false));
        dispatch(actions.resetPPMReportSearchPanelId());
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Creates a thunk that resets the stored PPM report request to the first result page.
 */
function fetchPPMreportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchPPMreportsResultsOffset`,
    async (
      {
        ppmFetchPpmReportsOffsetApi
      }, { getState, dispatch }
    ) => {
      const state = getState();
      if (state.ppmReport.ppmReportRequestBody
        && state.ppmReport.ppmReportResultData
      ) {
        let updatedRequest = state.ppmReport.ppmReportRequestBody;
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldAsiaSize(updatedRequest);
        updatedRequest = setFilterAsiaSize(updatedRequest);
        updatedRequest = setFieldShipToAddress(updatedRequest);
        delete updatedRequest.offset;
        updatedRequest.offset = '0';
        delete updatedRequest.filtersInResponse;

        dispatch(ppmFetchPpmReportsOffsetApi({ updatedRequest }));
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied first-page PPM report reload thunk.
 */
function ppmReportDataOffset() {
  return createAsyncThunk(
    `${name}/ppmReportDataOffset`,
    async (
      {
        ppmFetchPPMreportsResultsOffset,
        ppmFetchPpmReportsOffsetApi
      }, { dispatch }
    ) => {
      dispatch(ppmFetchPPMreportsResultsOffset({
        ppmFetchPpmReportsOffsetApi
      }));
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function fetchPpmReportNextApi() {
  return createAsyncThunk(
    `${name}/fetchPpmReportNextApi`,
    async ({
      requestBodyWithoutFilters,
      updatedRequest,
      currentResultSet,
      triggerJumpToPage
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestBodyWithoutFilters
      }, dispatch, getState)
        .then((response) => {
          dispatch(actions.updatePPMReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updatePPMReportNextResult({
            response, currentResultSet
          }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPageNextResultSet(state.ppmReport.rowsPerPage));
            dispatch(actions.updatePreviousResultSettoNull());
          }
        })
        .catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          console.log('NEXT Error ', error);
        });
    }
  );
}

/**
 * Creates a thunk that prepares the next cursor request for jump-to-page navigation.
 */
function fetchPpmReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchPpmReportJumpToPageNextResultSet`,
    async (
      { ppmFetchPpmReportNextApi },
      { getState, dispatch }
    ) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      if (state.ppmReport.ppmReportRequestBody
        && state.ppmReport.ppmReportResultData?.pages?.next
      ) {
        const currentResultSet = state.ppmReport.ppmReportResultData;
        let updatedRequest = state.ppmReport.ppmReportRequestBody;
        const searchResultNextOffset = state.ppmReport
          .ppmReportResultData.pages.next;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultNextOffset.slice(7, resNext)
        };
        let requestBodyWithoutFilters = { ...updatedRequest };
        requestBodyWithoutFilters = setFieldItemVas(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldAsiaSize(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFilterAsiaSize(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldShipToAddress(requestBodyWithoutFilters);
        delete requestBodyWithoutFilters.filtersInResponse;

        dispatch(ppmFetchPpmReportNextApi({
          requestBodyWithoutFilters,
          updatedRequest,
          currentResultSet
        }));
      }
    }
  );
}

/**
 * Creates a thunk that prepares the next PPM report page or jump-to-page request.
 */
function fetchPpmReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchPpmReportNextResultSet`,
    async (
      { ppmFetchPpmReportNextApi },
      { getState, dispatch }
    ) => {
      const state = getState();
      if (state.ppmReport.ppmReportRequestBody
        && state.ppmReport.ppmReportResultData?.pages?.next
      ) {
        const currentResultSet = state.ppmReport.ppmReportResultData;
        const currentJumpToPage = state.ppmReport.jumpToPage || 0;
        // Convert UI page indexes into the API's 1000-record page groups.
        const pageGroupSize = 1000 / state.ppmReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.ppmReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.ppmReport.ppmReportRequestBody;
        const searchResultNextOffset = state.ppmReport
          .ppmReportResultData.pages.next;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        let requestBodyWithoutFilters = { ...updatedRequest };
        requestBodyWithoutFilters = setFieldItemVas(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldAsiaSize(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFilterAsiaSize(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldShipToAddress(requestBodyWithoutFilters);
        delete requestBodyWithoutFilters.filtersInResponse;

        dispatch(ppmFetchPpmReportNextApi({
          requestBodyWithoutFilters,
          updatedRequest,
          currentResultSet,
          triggerJumpToPage
        }));
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied next-page PPM report thunk.
 */
function reportsDataNextSet() {
  return createAsyncThunk(
    `${name}/reportsDataNextSet`,
    async (
      {
        ppmFetchPpmReportNextResultSet,
        ppmFetchPpmReportNextApi
      },
      { dispatch }
    ) => {
      dispatch(ppmFetchPpmReportNextResultSet({ ppmFetchPpmReportNextApi }));
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function fetchPpmReportPrevApi() {
  return createAsyncThunk(
    `${name}/fetchPpmReportPrevApi`,
    async ({
      requestBody,
      updatedRequest,
      currentResultSet,
      triggerJumpToPage
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestBody
      }, dispatch, getState)
        .then((response) => {
          dispatch(actions.updatePPMReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updatePPMReportPrevResult({
            response, currentResultSet
          }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPagePrevResultSet(state.ppmReport.rowsPerPage));
            dispatch(actions.updateNextResultSettoNull());
          }
        })
        .catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          console.log('NEXT Error ', error);
        });
    }
  );
}

/**
 * Creates a thunk that prepares the previous cursor request for jump-to-page navigation.
 */
function fetchPpmReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchPpmReportJumpToPagePrevResultSet`,
    async (
      {
        ppmFetchPpmReportPrevApi
      }, { getState, dispatch }
    ) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.ppmReport?.ppmReportResultData?.pages?.prev) {
        const currentResultSet = state.ppmReport.ppmReportResultData;
        let updatedRequest = state.ppmReport.ppmReportRequestBody;
        const searchResultPrevOffset = state.ppmReport.ppmReportResultData.pages.prev;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        let requestBody = { ...updatedRequest };
        requestBody = setFieldItemVas(requestBody);
        requestBody = setFieldAsiaSize(requestBody);
        requestBody = setFilterAsiaSize(requestBody);
        requestBody = setFieldShipToAddress(requestBody);
        delete requestBody.filtersInResponse;

        dispatch(ppmFetchPpmReportPrevApi({
          requestBody,
          updatedRequest,
          currentResultSet
        }));
      }
    }
  );
}

/**
 * Creates a thunk that prepares the previous PPM report page or jump-to-page request.
 */
function fetchPpmReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchPpmReportPrevResultSet`,
    async (
      {
        ppmFetchPpmReportPrevApi
      }, { getState, dispatch }
    ) => {
      const state = getState();
      if (state.ppmReport?.ppmReportResultData?.pages?.prev) {
        const currentResultSet = state.ppmReport.ppmReportResultData;
        const currentJumpToPage = state.ppmReport.jumpToPage || 0;
        // Convert UI page indexes into the API's 1000-record page groups.
        const pageGroupSize = 1000 / state.ppmReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.ppmReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.ppmReport.ppmReportRequestBody;
        const searchResultPrevOffset = state.ppmReport.ppmReportResultData.pages.prev;
        // Cursor values arrive as query fragments; extract only the offset segment.
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        let requestBody = { ...updatedRequest };
        requestBody = setFieldItemVas(requestBody);
        requestBody = setFieldAsiaSize(requestBody);
        requestBody = setFilterAsiaSize(requestBody);
        requestBody = setFieldShipToAddress(requestBody);
        delete requestBody.filtersInResponse;

        dispatch(ppmFetchPpmReportPrevApi({
          requestBody,
          updatedRequest,
          currentResultSet,
          triggerJumpToPage
        }));
      }
    }
  );
}

/**
 * Creates a thunk wrapper that dispatches the supplied previous-page PPM report thunk.
 */
function reportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/reportsDataPrevSet`,
    async (
      {
        ppmFetchPpmReportPrevResultSet,
        ppmFetchPpmReportPrevApi
      }, { dispatch }
    ) => {
      dispatch(ppmFetchPpmReportPrevResultSet({ ppmFetchPpmReportPrevApi }));
    }
  );
}

// ppmReportResults -- end

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async ({
      payload, callback,
      shouldRequestFilterValues = false
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      const state = getState();
      const selectedFilters = {
        ...state.ppmReport.selectedFilters,
        [payload.filter]: payload.values
      };
      let requestBody = {
        ...state.ppmReport.ppmReportRequestBody,
        filter: mapFiltersToRequest(selectedFilters),
        offset: '0'
      };
      const selectedFilterKeys = Object.keys(selectedFilters)
        .filter((filter) => selectedFilters[filter]);
      if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
        getFiltersForRequest(requestBody, selectedFilterKeys);
      } else {
        delete requestBody.filtersInResponse;
      }
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldShipToAddress(requestBody);
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        requestBody = removeFieldAsiaSize(requestBody);
        requestBody = removeFieldShipToAddress(requestBody);
        dispatch(actions.updatePPMReportRequestBody(requestBody));
        dispatch(toggleSpinner(false));
        const flattenedResponse = getflattenResponse(response.data);
        dispatch(actions.updatePPMReportResults(flattenedResponse));
        dispatch(actions.updatePPMPage(0));
        dispatch(actions.setPpmReportFilterId());
        dispatch(actions.bulkUpdateReportFilterValues(payload));
        if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
          dispatch(actions.updateReportFiltersInResposne(requestBody.filtersInResponse));
        }
        if (response.data.filters) {
          dispatch(actions.updateReportFilterDropDownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
        }
        if (callback) {
          callback(response);
        }
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function fetchUnfilteredPOResults() {
  // TODO: callback function need add in props
  return createAsyncThunk(
    `${name}/fetchUnfilteredPOResults`,
    async (_, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      let requestBody = {
        ...state.ppmReport.ppmReportRequestBody,
        filter: [],
        offset: '0'
      };
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldShipToAddress(requestBody);

      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.clearFilters());
        dispatch(actions.resetPPMReportSearchPanelId());
        dispatch(actions.updateReportFiltersRequest([]));
        dispatch(actions.updatePPMReportResults(getflattenResponse(response.data)));
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Creates a thunk that applies follow-up filter dropdown, request, and result dispatches after
 * filter refresh.
 */
function aynscCallToDispatches() {
  return createAsyncThunk(
    `${name}/aynscCallToDispatches`,
    async (
      {
        response,
        filterDropdownValues,
        filtersInResponse,
        hasAppliedfiltersChanged,
        filtersToBeApplied,
        requestNewFiltersCallback

      },

      {
        dispatch
      }
    ) => {
      if (response.data.filters) {
        dispatch(actions.updateReportFilterDropDownValues({
          ...filterDropdownValues,
          ...getFilterValuesFromResponse(response.data.filters)
        }));
        dispatch(actions.updateReportFiltersInResposne(filtersInResponse));
      }
      if (hasAppliedfiltersChanged) {
        dispatch(actions.updateReportFiltersRequest(filtersToBeApplied));
        dispatch(actions.updatePPMReportResults(getflattenResponse(response.data)));
        dispatch(actions.updatePPMPage(0));
      }
      if (requestNewFiltersCallback) {
        requestNewFiltersCallback(response);
      }
    }
  );
}

/**
 * Creates a thunk that calls the ppmReportSearch API for the PPM report workflow and updates Redux
 * state.
 */
function requestForNewFilters() {
  return createAsyncThunk(
    `${name}/requestForNewFilters`,
    async ({
      filtersSelected, enableCheckboxForDefault,
      requestNewFiltersCallback,
      TaynscCallToDispatches
    }, { getState, dispatch }) => {
      const state = getState();
      let requestBody = { ...state.ppmReport.ppmReportRequestBody };
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldAsiaSize(requestBody);
      requestBody = setFilterAsiaSize(requestBody);
      requestBody = setFieldShipToAddress(requestBody);
      const {
        selectedFilters,
        filterDropdownValues
      } = state.ppmReport;
      const modifiedSelectedFilters = { ...selectedFilters };
      Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
        getModifiedSelectedFilters(filtersSelected, filter, modifiedSelectedFilters);
      });
      const filtersToBeRequested = [];
      const filtersInResponse = [];
      let filtersToBeApplied = null;

      const selectedFilterKeys = Object.keys(modifiedSelectedFilters)
        .filter((filter) => modifiedSelectedFilters[filter]);
      const appliedFiltersKeys = selectedFilterKeys.filter((filter) => (
        modifiedSelectedFilters[filter]?.length > 0
      ));
      // Re-query results only when enabled filters change the applied payload.
      const hasAppliedfiltersChanged = (!(requestBody.filter || []).every((filter) => (
        appliedFiltersKeys.includes(filter.fieldName)
      )));

      if (hasAppliedfiltersChanged) {
        filtersToBeApplied = mapFiltersToRequest(modifiedSelectedFilters);
        requestBody.filter = [...filtersToBeApplied];
      }

      selectedFilterKeys.forEach((filter) => {
        getSelectedFilterKeys(
          filter,
          modifiedSelectedFilters,
          filterDropdownValues,
          filtersToBeRequested,
          filtersInResponse
        );
      });
      if (filtersToBeRequested.length === 0) {
        delete requestBody.filtersInResponse;
      } else {
        requestBody.filtersInResponse = filtersToBeRequested;
      }
      requestBody.offset = '0';
      const selectedFilterData = {
        selectedFilterListForPPMReport: filtersSelected
      };
      dispatch(toggleSpinner(true));
      if (enableCheckboxForDefault) {
        dispatch(settingsActions.userProfileSettings({
          requestBody: {
            selectedFilterListForPPMReport: selectedFilterData.selectedFilterListForPPMReport
          }
        }));
      }
      request({
        api: 'ppmReportSearch',
        method: 'post',
        data: requestBody,
        cancellable: true
      }, dispatch, getState).then((response: Object) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.updatePPMPage(0));
        dispatch(actions.setPpmReportFilterId());
        dispatch(actions.resetPPMReportSearchPanelId());
        dispatch(actions.updatePPMReportOffsetInRequest('0'));
        dispatch(actions.updatePPMReportResults(getflattenResponse(response.data)));
        dispatch(actions.bulkUpdateSelectedFilters(filtersSelected));
        dispatch(TaynscCallToDispatches({
          response,
          filterDropdownValues,
          filtersInResponse,
          hasAppliedfiltersChanged,
          filtersToBeApplied,
          requestNewFiltersCallback
        }));
      }).catch((error: Object) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
        asyncCallToNewFiltersCallback(requestNewFiltersCallback, error);
      });
    }
  );
}

/**
 * Creates a thunk that calls the getGACReasonCode API for the PPM report workflow and updates Redux
 * state.
 */
function getGacReasonCode() {
  return createAsyncThunk(
    `${name}/getGacReasonCode`,
    async ({
      callback
    }, { dispatch }) => {
      const payload = {};
      dispatch(toggleSpinner(true));
      request({
        api: 'getGACReasonCode',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        payload.gacReasonCode = response.data;
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Creates async thunk actions exposed with the slice actions.
 */
function createExtraActions() {
  return {
    callPPMReportSearchAPI: callPPMReportSearchAPI(),
    fetchPOReports: fetchPOReports(),
    exportPOReport: exportPOReport(),
    callPPMReportSavedSearchAPI: callPPMReportSavedSearchAPI(),
    fetchSavedPPMReportResults: fetchSavedPPMReportResults(),
    fetchPpmReportsOffsetApi: fetchPpmReportsOffsetApi(),
    fetchPPMreportsResultsOffset: fetchPPMreportsResultsOffset(),
    ppmReportDataOffset: ppmReportDataOffset(),
    fetchPpmReportNextApi: fetchPpmReportNextApi(),
    fetchPpmReportNextResultSet: fetchPpmReportNextResultSet(),
    fetchPpmReportJumpToPageNextResultSet: fetchPpmReportJumpToPageNextResultSet(),
    reportsDataNextSet: reportsDataNextSet(),
    fetchPpmReportPrevApi: fetchPpmReportPrevApi(),
    fetchPpmReportPrevResultSet: fetchPpmReportPrevResultSet(),
    fetchPpmReportJumpToPagePrevResultSet: fetchPpmReportJumpToPagePrevResultSet(),
    reportsDataPrevSet: reportsDataPrevSet(),
    applyFilters: applyFilters(),
    fetchUnfilteredPOResults: fetchUnfilteredPOResults(),
    aynscCallToDispatches: aynscCallToDispatches(),
    requestForNewFilters: requestForNewFilters(),
    getGacReasonCode: getGacReasonCode()
  };
}

export const extraActions = createExtraActions();

export const ppmReportRequestActions = { ...actions, ...extraActions };
export const ppmReportRequestReducer = slice.reducer;
